import React, {useEffect, useRef} from 'react';
import {useQuery} from '@apollo/client';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';

import {getSearchFilters} from '@utils/filters';
import {getTodayDate, getEndOfTodayDate} from '@utils/date';
import HeaderRenderer from '@appComponents/HeaderRenderer';
import {FilterQueryWrapper, FilterUrlParamsWrapper} from '@appComponents/HOCFiltersQueryWrapper';
import {EventContentsTable} from '@modules/reports/EventContentsTable';
import {GetContentStreamingReportList, GetContentStreamingReportFilters} from '@graphql/reports/query';
import ReportsFilters from '@modules/reports/ReportsFilters';
import {showConstraintsModal, skipSearch} from '@modules/reports/utils/constraintsTables';
import useQueryCustom from '@utils/hooks/useQueryCustom';
import mapModulesToProps from '@utils/mapModulesToProps';
import navigation from '@constants/navigation';
import {showMessageBox} from '@utils/messageBox';

const MESSAGE_BOX_NAME = 'reportsMessage';

const DEFAULT_FILTER_VALUES = {
    search: null,
    from: getTodayDate(),
    to: getEndOfTodayDate(),
    propertyLicences: [],
    tournamentCategories: [],
    firstStageCategories: [],
    contentCategoriesLevel2: [],
    report: [],
};

export const ReportsIndex = ({filters = {}, Menu}) => {
    const queryVariables = {
        from: `${filters.from || DEFAULT_FILTER_VALUES.from}:00`,
        to: `${filters.to || DEFAULT_FILTER_VALUES.to}:00`,
    };
    const shouldSkipSearch = skipSearch({filters});
    const prevSkipSearchRef = useRef();

    const {
        data,
        error: contentsError,
        loading: contentsLoading = true,
    } = useQueryCustom({
        query: GetContentStreamingReportList,
        options: {
            variables: queryVariables,
            skip: shouldSkipSearch,
            refetch: filters.lastClickSubmit,
        },
    });

    const contents = contentsError ? [] : (data?.contentStreamingReportList ?? []);

    const {
        data: filtersForReporting = {},
        error: filtersError,
        loading: filtersLoading = true,
    } = useQuery(GetContentStreamingReportFilters);

    useEffect(() => {
        Menu.storeMenu(navigation.reportingCenter.key);
    }, []);

    useEffect(() => {
        if (shouldSkipSearch && (prevSkipSearchRef.current === undefined || !prevSkipSearchRef.current)) {
            showConstraintsModal(filters);
        }

        prevSkipSearchRef.current = shouldSkipSearch;
    }, [filters, shouldSkipSearch]);

    useEffect(() => {
        if (contentsError) {
            showMessageBox(
                MESSAGE_BOX_NAME,
                'Failed to fetch reports',
                `${contentsError}`,
                'error'
            );
        }
    }, [contentsError]);

    useEffect(() => {
        if (filtersError) {
            showMessageBox(
                MESSAGE_BOX_NAME,
                'Failed to fetch report filters',
                `${filtersError}`,
                'error'
            );
        }
    }, [filtersError]);

    return (
        <>
            <HeaderRenderer
                filters={ReportsFilters}
                filtersProps={{
                    data: filtersForReporting,
                    loading: filtersLoading,
                }}
                filtersButtonName='reports'
                loading={filtersLoading}
                messagesBoxNames={MESSAGE_BOX_NAME}
                pageTitle='Reporting for content streaming'
            />
            <EventContentsTable
                data={contents}
                loading={contentsLoading}
            />
        </>
    );
};

ReportsIndex.propTypes = {
    filters: PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string,
        search: PropTypes.string,
        propertyLicences: PropTypes.arrayOf(PropTypes.number),
        tournamentCategories: PropTypes.arrayOf(PropTypes.number),
        firstStageCategories: PropTypes.arrayOf(PropTypes.number),
        contentCategoriesLevel2: PropTypes.arrayOf(PropTypes.number),
        report: PropTypes.arrayOf(PropTypes.number),
    }),
    Menu: PropTypes.shape({
        storeMenu: PropTypes.func,
    }),
};

const ReportsIndexWrapped = FilterQueryWrapper(ReportsIndex, {
    filterUrls: ['reports.index'],
});

const mapStateToProps = (state) => ({
    filters: getSearchFilters(state, 'reports', DEFAULT_FILTER_VALUES),
});

export default connect(mapStateToProps, mapModulesToProps(['Menu']))(FilterUrlParamsWrapper(ReportsIndexWrapped, DEFAULT_FILTER_VALUES));
